// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - ÉDITEUR GRAFCET VISUEL
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/grafcet/GrafcetEditor.jsx
// Éditeur drag & drop pour créer des scénarios d'automatisation
// Papy 73+ - Joshua approved! 🚀
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Panel,
  MarkerType,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';

// Import des composants custom
import GrafcetNodeTypes from './nodes/GrafcetNodeTypes';
import GrafcetPalette from './components/GrafcetPalette';
import GrafcetToolbar from './components/GrafcetToolbar';
import GrafcetPropertiesPanel from './components/GrafcetPropertiesPanel';

// Styles
import './styles/GrafcetEditor.css';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = '/api/grafcet';

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#6366f1'
  },
  style: {
    strokeWidth: 2,
    stroke: '#6366f1'
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

function GrafcetEditorInner() {
  const reactFlowWrapper = useRef(null);
  const { project, fitView } = useReactFlow();
  
  // État du scénario
  const [scenarioId, setScenarioId] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  
  // État du canvas
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // État UI
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Données référentielles
  const [capteurs, setCapteurs] = useState([]);
  const [actionneurs, setActionneurs] = useState([]);
  const [sousDevisions, setSousDevisions] = useState([]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT INITIAL
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    loadScenarios();
    loadReferenceData();
  }, []);

  const loadScenarios = async () => {
    try {
      const res = await fetch(`${API_BASE}/scenarios`);
      const data = await res.json();
      if (data.success) {
        setScenarios(data.data);
      }
    } catch (error) {
      showMessage('error', 'Erreur chargement scénarios');
    }
  };

  const loadReferenceData = async () => {
    try {
      // Charger capteurs
      const resCapteurs = await fetch('/api/capteurs');
      const dataCapteurs = await resCapteurs.json();
      if (dataCapteurs.success) setCapteurs(dataCapteurs.data || []);
      
      // Charger sous-divisions
      const resSd = await fetch('/api/sous-divisions');
      const dataSd = await resSd.json();
      if (dataSd.success) setSousDevisions(dataSd.data || []);
    } catch (error) {
      console.error('Erreur chargement données référentielles:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT SCÉNARIO
  // ═══════════════════════════════════════════════════════════════════════════

  const loadScenario = async (id) => {
    if (!id) return;
    
    try {
      const res = await fetch(`${API_BASE}/scenarios/${id}`);
      const data = await res.json();
      
      if (data.success) {
        setScenario(data.data);
        setScenarioId(id);
        
        // Convertir étapes en nodes React Flow
        const flowNodes = data.data.etapes.map(etape => ({
          id: `etape-${etape.id}`,
          type: mapEtapeTypeToNodeType(etape.type_etape),
          position: { x: etape.position_x, y: etape.position_y },
          data: {
            label: etape.nom,
            etapeId: etape.id,
            type_etape: etape.type_etape,
            config: etape.config || {},
            etat_actuel: etape.etat_actuel,
            numero_etape: etape.numero_etape
          }
        }));
        
        // Convertir transitions en edges
        const flowEdges = data.data.transitions.map(trans => ({
          id: `trans-${trans.id}`,
          source: `etape-${trans.etape_source_id}`,
          target: `etape-${trans.etape_destination_id}`,
          label: trans.label || '',
          type: 'smoothstep',
          animated: true,
          data: {
            transitionId: trans.id,
            type_condition: trans.type_condition,
            condition_config: trans.condition_config || {},
            priorite: trans.priorite
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: getEdgeColor(trans.type_condition)
          },
          style: {
            strokeWidth: 2,
            stroke: getEdgeColor(trans.type_condition)
          }
        }));
        
        setNodes(flowNodes);
        setEdges(flowEdges);
        setHasChanges(false);
        
        setTimeout(() => fitView({ padding: 0.2 }), 100);
      }
    } catch (error) {
      showMessage('error', 'Erreur chargement scénario');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  const mapEtapeTypeToNodeType = (type) => {
    const mapping = {
      'debut': 'debutNode',
      'fin': 'finNode',
      'lecture_capteur': 'capteurNode',
      'condition': 'conditionNode',
      'action_simple': 'actionNode',
      'action_predefined': 'actionNode',
      'timer': 'timerNode',
      'parallele_debut': 'paralleleDebutNode',
      'parallele_fin': 'paralleleFinNode',
      'boucle': 'boucleNode',
      'ia_conseil': 'iaNode',
      'notification': 'notificationNode',
      'attente_validation': 'validationNode'
    };
    return mapping[type] || 'defaultNode';
  };

  const getEdgeColor = (type) => {
    const colors = {
      'toujours': '#6366f1',
      'timeout': '#f59e0b',
      'capteur_seuil': '#10b981',
      'feedback_ok': '#22c55e',
      'feedback_error': '#ef4444',
      'condition_custom': '#8b5cf6',
      'ia_evaluation': '#06b6d4',
      'meteo_seuil': '#0ea5e9',
      'date_atteinte': '#ec4899',
      'stade_culture': '#84cc16'
    };
    return colors[type] || '#6366f1';
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GESTION DES NODES
  // ═══════════════════════════════════════════════════════════════════════════

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    setIsPanelOpen(true);
  }, []);

  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setIsPanelOpen(true);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setIsPanelOpen(false);
  }, []);

  const onConnect = useCallback((params) => {
    // Créer une nouvelle transition
    const newEdge = {
      ...params,
      id: `trans-new-${Date.now()}`,
      type: 'smoothstep',
      animated: true,
      data: {
        type_condition: 'toujours',
        condition_config: {},
        priorite: 1,
        isNew: true
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#6366f1'
      },
      style: {
        strokeWidth: 2,
        stroke: '#6366f1'
      }
    };
    
    setEdges((eds) => addEdge(newEdge, eds));
    setHasChanges(true);
  }, [setEdges]);

  const onNodesDelete = useCallback((deleted) => {
    // Empêcher suppression des nodes debut/fin
    const protectedNodes = deleted.filter(n => 
      n.data.type_etape === 'debut' || n.data.type_etape === 'fin'
    );
    
    if (protectedNodes.length > 0) {
      showMessage('warning', 'Impossible de supprimer les étapes Début/Fin');
      return;
    }
    
    setHasChanges(true);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // DRAG & DROP DEPUIS PALETTE
  // ═══════════════════════════════════════════════════════════════════════════

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();

    const type = event.dataTransfer.getData('application/grafcet-node');
    if (!type) return;

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = project({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top
    });

    const nodeConfig = getDefaultNodeConfig(type);
    
    const newNode = {
      id: `etape-new-${Date.now()}`,
      type: mapEtapeTypeToNodeType(type),
      position,
      data: {
        label: nodeConfig.label,
        type_etape: type,
        config: nodeConfig.config,
        isNew: true
      }
    };

    setNodes((nds) => nds.concat(newNode));
    setHasChanges(true);
  }, [project, setNodes]);

  const getDefaultNodeConfig = (type) => {
    const configs = {
      'lecture_capteur': { label: 'Lire Capteur', config: { capteur_id: null } },
      'condition': { label: 'Condition', config: { operateur: 'lt', seuil: 50 } },
      'action_simple': { label: 'Action', config: { actionneur_id: null, commande: 'on' } },
      'timer': { label: 'Attendre', config: { duree: 60 } },
      'ia_conseil': { label: 'Conseil IA', config: { type_conseil: 'optimisation' } },
      'notification': { label: 'Notification', config: { message: '', destinataires: [] } },
      'attente_validation': { label: 'Validation', config: { timeout: 3600 } },
      'parallele_debut': { label: 'Début //', config: {} },
      'parallele_fin': { label: 'Fin //', config: {} },
      'boucle': { label: 'Boucle', config: { iterations: 3 } }
    };
    return configs[type] || { label: 'Étape', config: {} };
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SAUVEGARDE
  // ═══════════════════════════════════════════════════════════════════════════

  const saveScenario = async () => {
    if (!scenarioId) {
      showMessage('error', 'Aucun scénario sélectionné');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // 1. Sauvegarder/créer les nouvelles étapes
      for (const node of nodes) {
        if (node.data.isNew) {
          const res = await fetch(`${API_BASE}/scenarios/${scenarioId}/etapes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type_etape: node.data.type_etape,
              nom: node.data.label,
              position_x: Math.round(node.position.x),
              position_y: Math.round(node.position.y),
              config: node.data.config
            })
          });
          const data = await res.json();
          if (data.success) {
            // Mettre à jour l'ID du node
            node.id = `etape-${data.data.id}`;
            node.data.etapeId = data.data.id;
            node.data.isNew = false;
          }
        } else if (node.data.etapeId) {
          // Mettre à jour l'étape existante
          await fetch(`${API_BASE}/etapes/${node.data.etapeId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nom: node.data.label,
              position_x: Math.round(node.position.x),
              position_y: Math.round(node.position.y),
              config: node.data.config
            })
          });
        }
      }
      
      // 2. Sauvegarder/créer les nouvelles transitions
      for (const edge of edges) {
        const sourceId = parseInt(edge.source.replace('etape-', ''));
        const targetId = parseInt(edge.target.replace('etape-', ''));
        
        if (edge.data?.isNew) {
          const res = await fetch(`${API_BASE}/scenarios/${scenarioId}/transitions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              etape_source_id: sourceId,
              etape_destination_id: targetId,
              type_condition: edge.data.type_condition || 'toujours',
              condition_config: edge.data.condition_config || {},
              label: edge.label || '',
              priorite: edge.data.priorite || 1
            })
          });
          const data = await res.json();
          if (data.success) {
            edge.id = `trans-${data.data.id}`;
            edge.data.transitionId = data.data.id;
            edge.data.isNew = false;
          }
        } else if (edge.data?.transitionId) {
          await fetch(`${API_BASE}/transitions/${edge.data.transitionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type_condition: edge.data.type_condition,
              condition_config: edge.data.condition_config,
              label: edge.label,
              priorite: edge.data.priorite
            })
          });
        }
      }
      
      setHasChanges(false);
      showMessage('success', 'Scénario sauvegardé !');
      
      // Recharger pour sync
      await loadScenario(scenarioId);
      
    } catch (error) {
      showMessage('error', 'Erreur sauvegarde: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CRÉATION NOUVEAU SCÉNARIO
  // ═══════════════════════════════════════════════════════════════════════════

  const createNewScenario = async (nom, sousDevisionId, description) => {
    try {
      const res = await fetch(`${API_BASE}/scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom,
          sous_division_id: sousDevisionId,
          description
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        showMessage('success', `Scénario "${nom}" créé !`);
        await loadScenarios();
        await loadScenario(data.data.id);
      } else {
        showMessage('error', data.error);
      }
    } catch (error) {
      showMessage('error', 'Erreur création: ' + error.message);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EXÉCUTION
  // ═══════════════════════════════════════════════════════════════════════════

  const runScenario = async () => {
    if (!scenarioId) return;
    
    try {
      const res = await fetch(`${API_BASE}/scenarios/${scenarioId}/run`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage('success', 'Scénario démarré !');
        await loadScenario(scenarioId);
      } else {
        showMessage('error', data.error);
      }
    } catch (error) {
      showMessage('error', 'Erreur démarrage');
    }
  };

  const stopScenario = async () => {
    if (!scenarioId) return;
    
    try {
      const res = await fetch(`${API_BASE}/scenarios/${scenarioId}/stop`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        showMessage('success', 'Scénario arrêté');
        await loadScenario(scenarioId);
      }
    } catch (error) {
      showMessage('error', 'Erreur arrêt');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MISE À JOUR PROPRIÉTÉS
  // ═══════════════════════════════════════════════════════════════════════════

  const updateNodeData = (nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, ...newData }
          };
        }
        return node;
      })
    );
    setHasChanges(true);
  };

  const updateEdgeData = (edgeId, newData) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: { ...edge.data, ...newData },
            label: newData.label || edge.label,
            style: {
              ...edge.style,
              stroke: getEdgeColor(newData.type_condition || edge.data?.type_condition)
            },
            markerEnd: {
              ...edge.markerEnd,
              color: getEdgeColor(newData.type_condition || edge.data?.type_condition)
            }
          };
        }
        return edge;
      })
    );
    setHasChanges(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="grafcet-editor">
      {/* Header */}
      <GrafcetToolbar
        scenario={scenario}
        scenarios={scenarios}
        onSelectScenario={loadScenario}
        onNewScenario={createNewScenario}
        onSave={saveScenario}
        onRun={runScenario}
        onStop={stopScenario}
        isSaving={isSaving}
        hasChanges={hasChanges}
        sousDevisions={sousDevisions}
      />
      
      {/* Message */}
      {message.text && (
        <div className={`grafcet-message grafcet-message--${message.type}`}>
          {message.text}
        </div>
      )}
      
      {/* Zone principale */}
      <div className="grafcet-workspace">
        {/* Palette */}
        <GrafcetPalette />
        
        {/* Canvas */}
        <div className="grafcet-canvas" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onNodesDelete={onNodesDelete}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={GrafcetNodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode="Delete"
          >
            <Background variant="dots" gap={15} size={1} color="#94a3b8" />
            <Controls />
            <MiniMap 
              nodeColor={(n) => {
                if (n.data?.type_etape === 'debut') return '#22c55e';
                if (n.data?.type_etape === 'fin') return '#ef4444';
                if (n.data?.type_etape === 'condition') return '#f59e0b';
                if (n.data?.type_etape === 'action_simple') return '#6366f1';
                return '#64748b';
              }}
              maskColor="rgba(0,0,0,0.1)"
            />
            
            {!scenarioId && (
              <Panel position="top-center">
                <div className="grafcet-empty-state">
                  <span className="grafcet-empty-icon">📋</span>
                  <p>Sélectionnez ou créez un scénario pour commencer</p>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>
        
        {/* Panel propriétés */}
        {isPanelOpen && (selectedNode || selectedEdge) && (
          <GrafcetPropertiesPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            onUpdateNode={updateNodeData}
            onUpdateEdge={updateEdgeData}
            onClose={() => setIsPanelOpen(false)}
            capteurs={capteurs}
          />
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT AVEC PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

export default function GrafcetEditor() {
  return (
    <ReactFlowProvider>
      <GrafcetEditorInner />
    </ReactFlowProvider>
  );
}
